import React, { useState, useMemo } from "react";
import moment from "moment-timezone";
import SearchInput from "./SearchInput";
import CustomSelect from "./CustomSelect";
// import ArrowDownIcon from "../../assets/icons/arrowDown";
// import PrevIcon from "../../assets/icons/prev";
// import NextIcon from "../../assets/icons/next";
import { IoMdEye } from "react-icons/io";
import { getSubType } from "../../Utils/getUserUtitls";

const AttendanceTable = ({ data, handleSelectTime }) => {
  const [search, setSearch] = useState("");
  const [monthFilter, setMonthFilter] = useState(moment().format("MMM")); // default: current month
  const [yearFilter, setYearFilter] = useState(moment().year());
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 31;

  console.log("attendance data", data);

  // ✅ Allowed years (2020 to current year)
  const currentYear = moment().year();
  const yearOptions = Array.from({ length: currentYear - 2020 + 1 }, (_, i) => {
    const year = 2020 + i;
    return { label: year.toString(), value: year };
  });

  // ✅ Months: show all months if past year, only till current month if current year
  const monthOptions = useMemo(() => {
    const months = moment.monthsShort(); // ["Jan", "Feb", ... "Dec"]
    if (yearFilter === currentYear) {
      const currentMonthIndex = moment().month(); // 0-based
      return months.slice(0, currentMonthIndex + 1).map((m) => ({
        label: m,
        value: m,
      }));
    }
    return months.map((m) => ({ label: m, value: m }));
  }, [yearFilter]);

  // ✅ Generate full month dates with data (descending order)
  const allDates = useMemo(() => {
    const startOfMonth = moment(
      `${yearFilter}-${monthFilter}`,
      "YYYY-MMM"
    ).startOf("month");
    const endOfMonth = moment(startOfMonth).endOf("month");

    const days = Array.from({ length: endOfMonth.date() }, (_, i) =>
      moment(startOfMonth).add(i, "days")
    ).filter((day) => {
      // Hide future dates only for current month & year
      if (
        yearFilter === currentYear &&
        day.month() === moment().month() &&
        day.isAfter(moment(), "day")
      ) {
        return false;
      }
      return true;
    });

    return days.reverse(); // 🔥 descending order
  }, [yearFilter, monthFilter]);

  // Map attendance data into full month dates
  const mergedData = allDates.map((day) => {
    const record = data.find(
      (row) =>
        moment(row.date).format("DD MMM YYYY") === day.format("DD MMM YYYY")
    );
    return {
      date: day.format("DD MMM YYYY"),
      isSunday: day.day() === 0, // Sunday
      clock_in_time: record?.clock_in_time || null,
      clock_in_photo: record?.clock_in_photo || null,
      clock_out_time: record?.clock_out_time || null,
      clock_out_photo: record?.clock_out_photo || null,
      workingHours: record?.workingHours || null,
      location_data: record?.location_data || null,
      login_status: record?.status || null,
      location_id: record?.location_id || null,
      sub_type: record?.sub_type || null,
      is_holiday: record?.is_holiday || null,
      holiday_title: record?.holiday_title || null,
      attendance_status: record?.attendance_status || "Absent",
    };
  });

  // ✅ Filters
  const filteredData = mergedData.filter((row) => {
    return (
      row.date.toLowerCase().includes(search.toLowerCase()) &&
      (statusFilter ? statusFilter === row.attendance_status : true)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));

  // const handlePageChange = (page) => {
  //   if (page >= 1 && page <= totalPages) {
  //     setCurrentPage(page);
  //   }
  // };

  const currentRows = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const statusOptions = [
    { label: "All Status", value: "" },
    { label: "Present", value: "Present" },
    { label: "Absent", value: "Absent" },
    { label: "On Leave", value: "On Leave" },
  ];

  return (
    <div className="w-full rounded-lg">
      {/* Filters */}
      <div className="flex justify-between items-start md:flex-row gap-3 mb-4">
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by date..."
          fontSize="sm"
        />
        <div className="flex flex-grow-0 flex-shrink-0 gap-4">
          <CustomSelect
            options={monthOptions}
            placeholder="Month"
            value={monthFilter}
            onChange={(opt) => setMonthFilter(opt.value)}
            width="[150px]"
            height="36px"
            fontSize="sm"
            optionFontSize="sm"
          />
          <CustomSelect
            options={yearOptions}
            placeholder="Year"
            value={yearFilter}
            onChange={(opt) => setYearFilter(opt.value)}
            width="[120px]"
            height="36px"
            fontSize="sm"
            optionFontSize="sm"
          />
          <CustomSelect
            options={statusOptions}
            placeholder="Status"
            value={statusFilter}
            onChange={(opt) => setStatusFilter(opt.value)}
            width="[150px]"
            height="36px"
            fontSize="sm"
            optionFontSize="sm"
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="table-auto w-full text-sm">
          <thead>
            <tr className="h-[52px] bg-white border border-l-0 border-r-0 border-[#a48572]/10 text-[#142b70]">
              <th className="px-4 text-left text-sm font-semibold">Date</th>
              <th className="px-4 text-center text-sm font-semibold">
                Checked In
              </th>
              <th className="px-4 text-center text-sm font-semibold">
                Check Out
              </th>
              <th className="px-4 text-center text-sm font-semibold">
                Login Location
              </th>
              <th className="px-4 text-center text-sm font-semibold">
                Login Status
              </th>
              <th className="px-4 text-center text-sm font-semibold">Status</th>
              <th className="px-4 text-center text-sm font-semibold"></th>
            </tr>
          </thead>
          <tbody>
            {currentRows.length > 0 ? (
              currentRows.map((row, idx) =>
                row.isSunday ? (
                  <tr key={idx} className="bg-gray-100 text-gray-500">
                    <td className="relative text-left py-3 pl-3">
                      <p className="text-sm">{row.date}</p>
                    </td>
                    <td colSpan="6" className="text-center py-3 font-medium">
                      Sunday
                    </td>
                  </tr>
                ) : row.is_holiday ? (
                  <tr key={idx} className="bg-gray-100 text-gray-500">
                    <td className="relative text-left py-3 pl-3">
                      <p className="text-sm">{row.date}</p>
                    </td>
                    <td colSpan="6" className="text-center py-3 font-medium">
                      {row?.holiday_title}
                    </td>
                  </tr>
                ) : (
                  <tr key={idx} className="relative hover:bg-[#f9f9f9]">
                    <td className="relative text-left py-3 pl-3">
                      <p className="text-sm">{row.date}</p>
                    </td>
                    <td className="text-sm text-center py-3">
                      {row.clock_in_time
                        ? moment
                            .utc(row.clock_in_time)
                            .tz(
                              row.location_id === 1
                                ? "Asia/Dubai"
                                : row.location_id === 2
                                ? "Asia/Kolkata"
                                : "UTC"
                            )
                            .format("hh:mm A")
                        : "-"}
                    </td>
                    <td className="text-sm text-center py-3">
                      {row.clock_out_time
                        ? moment
                            .utc(row.clock_out_time)
                            .tz(
                              row.location_id === 1
                                ? "Asia/Dubai"
                                : row.location_id === 2
                                ? "Asia/Kolkata"
                                : "UTC"
                            )
                            .format("hh:mm A")
                        : "-"}
                    </td>
                    <td
                      className="text-sm text-center py-3"
                      title={
                        row.location_data
                          ? ["suburb", "town", "state"]
                              .map((k) => JSON.parse(row.location_data)?.[k])
                              .filter(Boolean)
                              .join(", ")
                          : "-"
                      }
                    >
                      {row.location_data
                        ? JSON.parse(row.location_data).country
                        : "-"}
                    </td>
                    <td className="text-sm text-center py-3">
                      {row.login_status === "on_time" ? (
                        <span className="flex justify-center items-center w-[90px] px-2 py-1 mx-auto rounded bg-[#32936f]/[0.16] text-[#519c66]">
                          On Time
                        </span>
                      ) : (
                        <span className="flex justify-center items-center w-[90px] px-2 py-1 mx-auto rounded bg-[#de2b26]/10 text-[#142b70]">
                          {row.login_status === "late"
                            ? "Late"
                            : row.login_status
                            ? "Half Day"
                            : "-"}
                        </span>
                      )}
                    </td>
                    <td className="text-sm text-center py-3 font-medium">
                      {row.attendance_status &&
                      row.attendance_status === "Casual Leave" ? (
                        <span className="flex flex-col justify-center items-center w-[150px] px-2 py-1 mx-auto rounded bg-[#de2b26]/10 text-[#142b70]">
                          {getSubType(row?.sub_type)}
                          <br />
                          {row.attendance_status}
                        </span>
                      ) : row.attendance_status === "Present" ? (
                        <span className="flex justify-center items-center w-[150px] px-2 py-1 mx-auto rounded bg-[#32936f]/[0.16] text-[#519c66]">
                          {row?.attendance_status}
                        </span>
                      ) : (
                        <span className="flex justify-center items-center w-[150px] px-2 py-1 mx-auto rounded bg-[#de2b26]/10 text-[#142b70]">
                          {row?.attendance_status}
                        </span>
                      )}
                    </td>
                    <td className="text-sm text-center py-3 font-medium">
                      {(row.clock_in_photo || row.clock_out_photo) && (
                        <IoMdEye
                          className="text-[#c30f0a] text-lg cursor-pointer"
                          onClick={() =>
                            handleSelectTime({
                              checkInImg: row.clock_in_photo,
                              checkOutImg: row.clock_out_photo,
                              text: "Check In/Out Image",
                            })
                          }
                        />
                      )}
                    </td>
                  </tr>
                )
              )
            ) : (
              <tr>
                <td colSpan="6" className="text-center py-4">
                  No results found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {/* <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-4">
        <div className="relative w-[190px] h-[30px] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex items-center h-[26px] px-[10px] rounded-lg bg-[#f9f9f9] cursor-pointer relative">
              <select
                value={currentPage}
                onChange={(e) => handlePageChange(Number(e.target.value))}
                className="bg-transparent text-xs focus:outline-none cursor-pointer appearance-none pr-5"
              >
                {Array.from({ length: totalPages }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
              <ArrowDownIcon
                iconClass="absolute right-2 pointer-events-none"
                color="#142b70"
              />
            </div>
            <p className="text-sm text-[#142b70]/80">of {totalPages} pages</p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="disabled:opacity-40"
            >
              <PrevIcon color="#142b70" />
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="disabled:opacity-40"
            >
              <NextIcon color="#142b70" />
            </button>
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default AttendanceTable;
